/**
 * Repaints the Rebuy bundle popup (widget 289411) with THIS store's pricing.
 *
 * Rebuy renders its own prices, straight from its product API. The rest of the storefront does not:
 * `assets/apply-discount-cards.js` (loaded globally from layout/theme.liquid) rewrites every
 * `.js-product-card` at runtime from the `data-*` that `snippets/discount-attribute.liquid` writes,
 * so the card, the cart drawer and the checkout all show the store discount. Left alone, the popup
 * is the one surface in the buying flow quoting a different number than the cart it is about to
 * write to.
 *
 * The bridge: for each product the popup offers, fetch that product's card markup
 * (`?view=discountdata`), copy its dataset onto the popup's `.js-product-card`, and let the theme's
 * own discount utility do the arithmetic. The badge ("Active sleepers", …) rides along in the same
 * response, so it is applied here too.
 *
 * WHY IT IS A FILE AND NOT INLINE: this was an inline <script> in `blocks/rx-popup.liquid`, which is
 * a product-page-only block — so the popup was correctly priced on a product page and would have
 * quoted raw Rebuy prices anywhere else. It now loads from `snippets/sl-bundle-upsell.liquid`, i.e.
 * exactly wherever the widget is mounted, product page included.
 *
 * TWO ENTRY POINTS, because the widget fills up differently on each surface:
 *
 *   product page  `rebuy.ready` already carries the products — Rebuy resolved them from the
 *                 container's `data-rebuy-shopify-product-ids` before this module ever ran.
 *   card page     `rebuy.ready` fires ONCE, with an EMPTY product list, and never again. The
 *                 products arrive later, when assets/sl-bundle-upsell-cards.js primes the widget
 *                 for the add in flight, so that module announces them on `PRODUCTS_EVENT`.
 *
 * Keying on `rebuy.ready` alone is what the first version did, and it silently skipped the whole
 * rewrite on card pages: measured on home 2026-09-04 — zero `?view=discountdata` requests, no
 * `(N% off)`, no "Save $X", no badge, while the card behind the popup showed all three.
 *
 * Both paths land before the popup is visible, so the shopper never sees a price correct itself.
 */

/** @type {number} Rebuy widget id for the bundle popup. */
const WIDGET_ID = 289411;

/** @type {string} Emitted by assets/sl-bundle-upsell-cards.js once a card-page prime has resolved. */
const PRODUCTS_EVENT = 'sl:bundle-upsell:products';

/**
 * @type {number} How long to keep looking for a product's card inside the popup.
 *
 * Rebuy paints the grid on its own schedule, and the two entry points arrive at different points in
 * that cycle — the fixed 500ms wait this used to do was tuned for the product page and is simply a
 * guess on a card page, where the products land just before Rebuy shows the popup. Polling for the
 * node the write targets is what makes the same code correct on both.
 */
const CARD_WAIT = 3000;

/** @type {number} Gap between attempts to find the card node. */
const CARD_POLL = 100;

/**
 * @param {string} selector
 * @returns {Promise<Element | null>} The node once Rebuy has painted it, or null if it never comes.
 */
async function waitForCard(selector) {
  const deadline = Date.now() + CARD_WAIT;
  do {
    const found = document.querySelector(selector);
    if (found) return found;
    await new Promise((resolve) => setTimeout(resolve, CARD_POLL));
  } while (Date.now() < deadline);

  return null;
}

/**
 * Copies one product's store-discount dataset onto its card inside the popup.
 * @param {{ id: number, handle: string }} product
 */
async function applyStorePricing(product) {
  const response = await fetch(`/products/${product.handle}?view=discountdata`);
  if (!response.ok) throw new Error(`discountdata ${response.status} for ${product.handle}`);

  const metadata = await response.json();
  if (!metadata?.data) return;

  const card = await waitForCard(`#rebuy-widget-${WIDGET_ID} .product-id-${product.id} .js-product-card`);
  if (!(card instanceof HTMLElement)) return;

  if (metadata.badge && metadata.badge.trim() !== '') {
    const badge = card.querySelector('.sl-product-card__badge');
    const tag = badge?.querySelector('.sl-product-tag');
    if (badge && tag) {
      badge.classList.remove('hidden');
      tag.textContent = metadata.badge;
      tag.classList.remove('hidden');
    }
  }

  const parsed = document.createElement('div');
  parsed.innerHTML = metadata.data;
  const source = parsed.querySelector('.js-product-card');
  if (!(source instanceof HTMLElement)) return;

  for (const key in source.dataset) card.dataset[key] = source.dataset[key];

  // The theme's own discount pass — the same one every other card on the page goes through.
  window.Theme?._applyDiscountToCards?.(document.querySelector(`#rebuy-widget-${WIDGET_ID}`));
}

/**
 * @param {any} widget The Rebuy widget, whichever entry point announced it.
 */
function priceEveryOffer(widget) {
  if (parseInt(widget?.id, 10) !== WIDGET_ID) return;

  for (const product of widget.data?.products ?? []) {
    applyStorePricing(product).catch((error) => {
      // A product left at Rebuy's own price is worse than the store price but still a real price;
      // it must never take the popup down with it.
      console.error(`Applying store pricing to ${product.handle} failed:`, error);
    });
  }
}

// Product page: the products are already on the event.
document.addEventListener('rebuy.ready', (event) => {
  priceEveryOffer(/** @type {any} */ (event).detail?.widget);
});

// Card page: the products only exist once a prime has resolved.
document.addEventListener(PRODUCTS_EVENT, (event) => {
  priceEveryOffer(/** @type {any} */ (event).detail?.widget);
});
